/**
 * ProductsPanel - Manage the product catalog in Settings.
 * Admin creates/edits/deletes products keyed by (product_code, owner), where
 * owner is a curated Source or a "Has Catalog" Sending Florist.
 * Each size variant carries a free-text recipe, pasted from the source.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Plus, Search, Pencil, Trash2, X, Package, Image as ImageIcon, Loader2, Ruler, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { cloudProductPhotoUrl } from '@/utils/productReference';
import {
  createProduct,
  deleteProduct,
  deleteProductPhoto,
  updateProduct,
  uploadProductPhoto,
} from '@/api/products';
import { useProductCatalog, useProductCatalogSnapshot, validProduct } from '@/hooks/useProductCatalog';
import SizesBreakout from './SizesBreakout';
import { useOrderSizes } from '@/hooks/useOrderSizes';
import SizeChoicesUnavailable from '@/components/SizeChoicesUnavailable';
const EMPTY_VARIANT = { size_label: '', code: '', recipe_text: '', retail_price: '' };

/* ── PhotoSlot ── one upload target: the product default (size=null) or a size
   override keyed by a variant's free-text label. Uploads immediately via the
   dedicated endpoints, independent of the form's save. When `disabled`, shows a
   hint instead (e.g. product not saved yet, or the size not named). */
const PhotoSlot = ({ productId, size, label, currentPath, updatedAt, onPhotoAction, actionDisabled, disabled, hint, className = '' }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const filled = !!currentPath;
  // Cache-buster: the src URL is stable but its bytes change on replace.
  const src = cloudProductPhotoUrl(productId, size, updatedAt);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await onPhotoAction(size, file);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await onPhotoAction(size, null);
    } finally {
      setBusy(false);
    }
  };

  if (disabled) {
    return (
      <div className={`rounded-lg border border-dashed border-stone-200 bg-stone-50 flex items-center justify-center text-center p-2 ${className}`}>
        <span className="text-[11px] text-stone-400 leading-tight">{hint}</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} data-testid={`photo-slot-${size || 'default'}`}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy || actionDisabled}
        className={`w-full h-full rounded-lg border overflow-hidden flex items-center justify-center transition-colors
          ${filled ? 'border-stone-200' : 'border-dashed border-stone-300 hover:border-stone-400 bg-stone-50'}`}
        title={filled ? `Replace ${label} photo` : `Add ${label} photo`}
      >
        {busy ? (
          <Loader2 className="h-5 w-5 text-stone-400 animate-spin" />
        ) : filled ? (
          <img src={src} alt={label} className="w-full h-full object-cover" />
        ) : (
          <span className="flex flex-col items-center gap-1 text-stone-300">
            <ImageIcon className="h-5 w-5" />
            <span className="text-[10px] text-stone-400">{label}</span>
          </span>
        )}
      </button>
      {filled && !busy && (
        <button
          type="button"
          onClick={remove}
          disabled={actionDisabled}
          title="Remove photo"
          data-testid={`photo-remove-${size || 'default'}`}
          className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-stone-200 shadow-sm p-0.5 text-stone-400 hover:text-red-500"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        disabled={busy || actionDisabled}
        className="hidden"
        onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }}
      />
    </div>
  );
};

/* ── Variant Editor (one size: label, price, recipe text, and its photo) ──
   The photo sits to the right, spanning the row's height, and is keyed by this
   variant's own size label (whatever text it holds). Photos need a saved product
   and a named size, so the slot shows a hint until both exist. */
const VariantEditor = ({
  variant, index, onUpdate, onRemove, canRemove, sizes,
  productId, sizePhotos, updatedAt, onPhotoAction, actionDisabled, canUploadPhoto,
}) => {
  const updateField = (field, value) => onUpdate(index, field, value);
  const sizeKey = (variant.size_label || '').trim();
  // Offer the authoritative tiers; keep any pre-existing off-list value selectable
  // so editing a legacy variant never silently drops its size.
  const sizeOptions = variant.size_label && !sizes.includes(variant.size_label)
    ? [...sizes, variant.size_label]
    : sizes;

  return (
    <div className="border border-stone-100 rounded-lg p-3 bg-stone-50/30" data-testid={`variant-editor-${index}`}>
      <div className="flex gap-3 items-stretch">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex gap-2 items-center">
            <select
              disabled={!sizes.length}
              value={variant.size_label || ''}
              onChange={e => updateField('size_label', e.target.value)}
              className="w-28 text-xs h-8 rounded-md border border-stone-200 bg-white px-2 focus:outline-none focus:ring-2 focus:ring-stone-400"
              data-testid={`variant-size-${index}`}
            >
              <option value="">— Size —</option>
              {sizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {!sizes.length && <SizeChoicesUnavailable />}
            <Input
              value={variant.code || ''}
              onChange={e => updateField('code', e.target.value)}
              placeholder="Code (e.g., T152-2A)"
              className="flex-1 min-w-0 text-xs h-8 font-mono"
              data-testid={`variant-code-${index}`}
            />
            <Input
              value={variant.retail_price}
              onChange={e => updateField('retail_price', e.target.value)}
              placeholder="Price"
              className="w-20 text-xs h-8"
              type="number"
              step="0.01"
              data-testid={`variant-price-${index}`}
            />
            {canRemove && (
              <button onClick={() => onRemove(index)} className="text-stone-400 hover:text-red-500 ml-auto">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Textarea
            value={variant.recipe_text || ''}
            onChange={e => updateField('recipe_text', e.target.value)}
            placeholder="Paste the recipe for this size…"
            rows={4}
            className="text-xs font-mono"
            data-testid={`variant-recipe-${index}`}
          />
        </div>

        {/* Photo for this size — spans the row height */}
        <div className="w-28 flex-shrink-0">
          {!canUploadPhoto ? (
            <PhotoSlot disabled hint="Save product to add its photo" className="w-full h-full" />
          ) : !sizeKey ? (
            <PhotoSlot disabled hint="Name the size to add its photo" className="w-full h-full" />
          ) : (
            <PhotoSlot
              productId={productId}
              size={sizeKey}
              label={sizeKey}
              currentPath={(sizePhotos || {})[sizeKey]}
              updatedAt={updatedAt}
              onPhotoAction={onPhotoAction}
              actionDisabled={actionDisabled}
              className="w-full h-full"
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ── ProductForm ── create/edit a product, opened in the settings-breakout.
   Self-contained (owns its form + live photo state) because the breakout snapshots
   the node at open time. Photos save immediately via their own endpoints; `product`
   holds the live photo state, refetched after each upload. Brings its own Card. */
const productDraft = product => product ? {
  product_code: product.product_code,
  owner: product.owner || null,
  product_name: product.product_name || '',
  variants: product.variants?.length ? product.variants.map(v => ({
    size_label: v.size_label || '', code: v.code || '', recipe_text: v.recipe_text || '', retail_price: v.retail_price ?? '',
  })) : [{ ...EMPTY_VARIANT }],
  notes: product.notes || '',
} : { product_code: '', owner: null, product_name: '', variants: [{ ...EMPTY_VARIANT }], notes: '' };

const ProductForm = ({ initialProduct, controller, opening, onClose }) => {
  const [editingId, setEditingId] = useState(initialProduct?.id || null);
  const catalog = useProductCatalogSnapshot(controller);
  const ownerOptions = catalog.owners || [];
  const sizes = useOrderSizes();
  const [product, setProduct] = useState(initialProduct || null);   // live photo state
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [form, storeForm] = useState(() => productDraft(initialProduct));
  const draftRevision = useRef(0);
  const mounted = useRef(false);
  const request = useRef(null);
  const readVersion = useRef(0);
  const [notice, setNotice] = useState('');
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [review, setReview] = useState(null);
  const [detailReady, setDetailReady] = useState(!initialProduct);
  const current = () => mounted.current && opening.current();
  const setForm = update => { ++draftRevision.current; storeForm(update); };
  const operationKey = editingId || '$create';
  const uncertain = catalog.writes[operationKey]?.status === 'unknown';
  const actionDisabled = !catalog.active || !opening.current() || saving || refreshing || !detailReady || refreshFailed || controller.blocked(operationKey);

  const refreshProduct = async ({ initialize = false, inspect = false } = {}) => {
    if (!current() || !editingId) return false;
    const version = ++readVersion.current;
    try {
      const observed = await controller.readProduct(editingId);
      if (!current() || version !== readVersion.current) return false;
      setProduct(observed);
      controller.observeProduct(observed);
      if (initialize && draftRevision.current === 0) storeForm(productDraft(observed));
      if (inspect) setReview(observed);
      setDetailReady(true);
      setRefreshFailed(false);
      return true;
    } catch {
      if (current() && version === readVersion.current) setRefreshFailed(true);
      return false;
    }
  };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  // A replacement editor waits for an earlier write to settle before acquiring
  // its baseline. Photo refreshes never replace the separately owned text draft.
  const waitingForWrite = catalog.writes[operationKey]?.status === 'pending';
  useEffect(() => {
    if (editingId && !waitingForWrite && !detailReady) refreshProduct({ initialize: true });
    // This acquisition belongs to this opening; callbacks read its live refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, waitingForWrite, detailReady]);

  const photoAction = async (size, file) => {
    if (!current() || request.current || actionDisabled) return;
    const attempt = {};
    request.current = attempt;
    ++readVersion.current;
    setSaving(true); setReview(null); setNotice('');
    const result = await controller.mutation(editingId,
      config => file ? uploadProductPhoto(editingId, size, file, config) : deleteProductPhoto(editingId, size, config),
      value => value?.size === size && (file ? typeof value.storage_path === 'string' && !!value.storage_path : value.deleted === true));
    if (current()) {
      if (result.kind === 'accepted') {
        setNotice('Photo change saved. Your product draft is unchanged.');
        await refreshProduct();
      } else if (result.kind === 'unknown') setNotice('Photo outcome is uncertain. Refresh product and review its current state before another change.');
      else if (result.kind === 'rejected') setNotice('Photo change was refused. Your draft is unchanged.');
    }
    if (result.kind === 'accepted') await controller.refreshList();
    if (request.current === attempt) request.current = null;
    if (current()) setSaving(false);
  };

  const inspectProduct = async () => {
    if (!current() || request.current) return;
    const attempt = {};
    request.current = attempt;
    setRefreshing(true);
    const observed = editingId ? await refreshProduct({ inspect: uncertain }) : true;
    if (current() && request.current === attempt) {
      const listed = await controller.refreshList();
      if (current() && request.current === attempt) setRefreshFailed(!observed || !listed);
    }
    if (request.current === attempt) request.current = null;
    if (current()) setRefreshing(false);
  };

  const ownerKey = (o) => (o && o.type && o.id ? `${o.type}:${o.id}` : '');
  const ownerFromKey = (k) => {
    const idx = (k || '').indexOf(':');
    if (idx < 0) return null;
    return { type: k.slice(0, idx), id: k.slice(idx + 1) };
  };

  const addVariant = () => setForm(f => ({ ...f, variants: [...f.variants, { ...EMPTY_VARIANT }] }));
  const removeVariant = (i) => setForm(f => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));
  const updateVariant = (i, field, value) => setForm(f => {
    const variants = [...f.variants];
    variants[i] = { ...variants[i], [field]: value };
    return { ...f, variants };
  });

  const handleSubmit = async () => {
    if (!current() || request.current || actionDisabled) return;
    if (!form.product_code.trim() || !form.owner) {
      toast.error('Product code and owner are required');
      return;
    }
    const payload = {
      ...form,
      variants: form.variants
        .filter(v => v.size_label.trim())
        .map(v => ({
          size_label: v.size_label,
          code: v.code?.trim() || null,
          recipe_text: v.recipe_text?.trim() || null,
          retail_price: v.retail_price !== '' ? parseFloat(v.retail_price) || null : null,
        })),
    };
    const submittedRevision = draftRevision.current;
    const attempt = {};
    request.current = attempt;
    ++readVersion.current;
    setSaving(true); setReview(null); setNotice('');
    const result = await controller.mutation(operationKey,
      config => editingId ? updateProduct(editingId, payload, config) : createProduct(payload, config),
      value => validProduct(value) && (!editingId || value.id === editingId)
        && value.product_code === payload.product_code
        && value.owner.type === payload.owner.type && value.owner.id === payload.owner.id);
    if (result.kind === 'accepted') {
      controller.observeProduct(result.value);
      if (current()) {
        setEditingId(result.value.id);
        opening.productId = result.value.id;
        setProduct(result.value);
        setNotice('Product saved. Any changes made after submission are still unsaved.');
      }
      const refreshed = await controller.refreshList();
      if (current()) {
        setRefreshFailed(!refreshed);
        if (refreshed && draftRevision.current === submittedRevision) onClose();
      }
    } else if (current() && result.kind === 'unknown') {
      setNotice('Save outcome is uncertain. Your input is retained; the request will not be repeated. Inspect the catalog before deciding what to do next.');
    } else if (current() && result.kind === 'rejected') setNotice('Save was refused. Your input is retained; review it before trying again.');
    if (request.current === attempt) request.current = null;
    if (current()) setSaving(false);
  };

  return (
    <Card data-testid="product-form">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{editingId ? 'Edit Product' : 'New Product'}</CardTitle>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-600" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!catalog.active && <p role="status">This product session has ended. Reopen Products to continue.</p>}
        {notice && <p role="status">{notice}</p>}
        {refreshFailed && <p role="alert">The current product or catalog could not be refreshed. Any acknowledged change remains saved. Retry the read below.</p>}
        {(refreshFailed || uncertain) && <Button variant="outline" disabled={saving || refreshing || !catalog.active} onClick={inspectProduct}>Refresh product</Button>}
        {uncertain && !editingId && <p>Creation cannot be retried here because no product identity was acknowledged. Refreshing the catalog does not prove which request created a product.</p>}
        {uncertain && review && <div>
          <p>Current saved product — compare this observation with your draft. It is not a receipt for the earlier request.</p>
          <ProductDetail product={review} ownerLabel={owner => ownerOptions.find(row => row.id === owner?.id && row.type === owner?.type)?.name || '(unknown owner)'} />
          <Button variant="outline" disabled={refreshing} onClick={() => { if (current() && !request.current) { controller.reviewed(editingId); setReview(null); setNotice('Current product reviewed. Your draft is retained for a deliberate new save.'); } }}>Keep draft and allow a new save</Button>
        </div>}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-stretch">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
            {/* Code + Name stacked in one column */}
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Product Code *</label>
                <Input
                  value={form.product_code}
                  onChange={e => setForm(f => ({ ...f, product_code: e.target.value }))}
                  placeholder="e.g., T67-1A"
                  data-testid="product-code-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Product Name</label>
                <Input
                  value={form.product_name}
                  onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
                  placeholder="e.g., Garden Rhapsody"
                  data-testid="product-name-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Owner *</label>
              <select
                value={ownerKey(form.owner)}
                onChange={e => setForm(f => ({ ...f, owner: ownerFromKey(e.target.value) }))}
                className="flex h-10 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
                data-testid="product-owner-input"
              >
                <option value="">— Select an owner —</option>
                <optgroup label="Networks">
                  {ownerOptions.filter(o => o.type === 'network').map(o => (
                    <option key={`network:${o.id}`} value={`network:${o.id}`}>{o.name}</option>
                  ))}
                </optgroup>
                <optgroup label="Florists (Has Catalog)">
                  {ownerOptions.filter(o => o.type === 'florist').map(o => (
                    <option key={`florist:${o.id}`} value={`florist:${o.id}`}>{o.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>

          {/* Default photo — spans the height of the stacked fields. */}
          <div
            className="flex-shrink-0 flex flex-col w-32"
            title="Shown when an order's size has no photo of its own, or the order has no size."
          >
            <label className="text-xs font-medium text-stone-500 mb-1 block">Default Photo</label>
            {editingId ? (
              <PhotoSlot
                productId={editingId}
                size={null}
                label="Default"
                currentPath={product?.reference_photo_url}
                updatedAt={product?.updated_at}
                onPhotoAction={photoAction}
                actionDisabled={actionDisabled}
                className="w-full flex-1 min-h-[6rem]"
              />
            ) : (
              <div className="w-full flex-1 min-h-[6rem] rounded-lg border border-dashed border-stone-200 bg-stone-50 flex items-center justify-center text-center p-2">
                <span className="text-[11px] text-stone-400 leading-tight">Save product first</span>
              </div>
            )}
          </div>
        </div>

        {/* Variants */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-stone-500">Size Variants</label>
            <button onClick={addVariant} className="text-xs text-primary hover:underline" data-testid="add-variant-btn">
              + Add size
            </button>
          </div>
          <div className="space-y-2">
            {form.variants.map((v, i) => (
              <VariantEditor
                key={`variant-${i}`}
                variant={v}
                index={i}
                onUpdate={updateVariant}
                onRemove={removeVariant}
                canRemove={form.variants.length > 1}
                sizes={sizes}
                productId={editingId}
                sizePhotos={product?.size_photos}
                updatedAt={product?.updated_at}
                onPhotoAction={photoAction}
                actionDisabled={actionDisabled}
                canUploadPhoto={!!editingId}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-stone-500 mb-1 block">Notes</label>
          <Textarea
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Designer tips, vase type, color palette..."
            rows={2}
            className="resize-none"
            data-testid="product-notes-input"
          />
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={actionDisabled} data-testid="product-save-btn">
            {saving ? 'Saving…' : (editingId ? 'Update' : 'Create')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* ── ProductDetail ── read-only product view for the settings-breakout, shown
   when a product row is clicked (the row highlights; nothing expands in place). */
const ProductDetail = ({ product, ownerLabel, onEdit, onClose }) => {
  const sizePhotos = product.size_photos || {};
  const photoUrl = (size) =>
    cloudProductPhotoUrl(product.id, size, product.updated_at);

  return (
    <Card data-testid="product-detail">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-2">
        <div className="min-w-0">
          <CardTitle className="text-base font-mono">{product.product_code}</CardTitle>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${product.owner?.type === 'florist' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}`}>
              {ownerLabel(product.owner)}{product.owner?.type === 'florist' ? ' · Florist' : ''}
            </span>
            {product.product_name && <span className="text-sm text-stone-700">{product.product_name}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onEdit && <button onClick={onEdit} className="text-xs text-primary hover:underline" data-testid="detail-edit-btn">Edit</button>}
          {onClose && <button onClick={onClose} className="text-stone-400 hover:text-stone-600" aria-label="Close">
            <X className="h-4 w-4" />
          </button>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {product.reference_photo_url && (
          <img src={photoUrl(null)} alt="Default" className="w-full max-h-48 object-contain rounded-lg border border-stone-100 bg-stone-50" />
        )}
        {product.variants?.length ? (
          <div className="space-y-3">
            {product.variants.map((v, i) => (
              <div key={v.size_label || i} className="border-b border-stone-100 last:border-0 pb-3 last:pb-0 flex items-start gap-3">
                {sizePhotos[v.size_label] && (
                  <img src={photoUrl(v.size_label)} alt={v.size_label} className="w-16 h-16 object-cover rounded-md border border-stone-100 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-medium text-stone-700">
                      {v.size_label}
                      {v.code && <span className="ml-2 font-mono text-xs text-stone-400">{v.code}</span>}
                    </span>
                    <span className="text-sm font-medium text-stone-700 flex-shrink-0">
                      {v.retail_price != null ? `$${v.retail_price.toFixed(2)}` : '—'}
                    </span>
                  </div>
                  {v.recipe_text && <pre className="text-xs text-stone-600 whitespace-pre-wrap font-sans">{v.recipe_text}</pre>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-stone-400">No size variants defined</p>
        )}
        {product.notes && <p className="text-xs text-stone-500 italic border-t border-stone-100 pt-2">{product.notes}</p>}
      </CardContent>
    </Card>
  );
};

// The breakout host keeps the node supplied when it opens. Subscribe here so a
// predecessor's pending write cannot leave this detail stuck on an older read.
const ProductDetailOpening = ({ controller, opening, onEdit, onClose }) => {
  const catalog = useProductCatalogSnapshot(controller);
  const [product, setProduct] = useState(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const waiting = catalog.writes[opening.productId]?.status === 'pending';
  useEffect(() => {
    let disposed = false;
    setProduct(null); setFailed(false);
    if (waiting || !opening.current()) return () => { disposed = true; };
    controller.readProduct(opening.productId).then(observed => {
      if (disposed || !opening.current()) return;
      controller.observeProduct(observed);
      setProduct(observed);
    }).catch(() => { if (!disposed && opening.current()) setFailed(true); });
    return () => { disposed = true; };
  }, [controller, opening, waiting, retry]);
  if (!catalog.active) return <p>This product session has ended. Reopen Products to continue.</p>;
  if (!product) return <Card><CardContent>
    <p role={failed ? 'alert' : 'status'}>{failed ? 'Product details are unavailable.' : 'Loading product...'}</p>
    {failed && <Button onClick={() => setRetry(value => value + 1)}>Retry product details</Button>}
    <Button onClick={onClose}>Close</Button>
  </CardContent></Card>;
  const ownerLabel = owner => catalog.owners?.find(row => row.id === owner?.id && row.type === owner?.type)?.name || '(unknown owner)';
  return <ProductDetail product={product} ownerLabel={ownerLabel} onClose={onClose} onEdit={() => {
    if (opening.current()) onEdit(product);
  }} />;
};

/* ── Main ProductsPanel ──────────────────────────────────── */
const ProductsPanel = ({ onOpenBreakout, onCloseBreakout }) => {
  const catalog = useProductCatalog();
  const { controller } = catalog;
  const products = catalog.products || [];
  const ownerOptions = catalog.owners || [];
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);   // product whose detail is open in the breakout
  const [armedDeleteProductId, setArmedDeleteProductId] = useState(null);
  const deleteArm = useRef(null);
  const deleteTimer = useRef(null);
  const activeOpening = useRef(null);
  useEffect(() => () => { clearTimeout(deleteTimer.current); deleteArm.current = null; }, [controller]);

  // Owner id→name for display.
  const ownerLabel = (o) => {
    if (!o) return '—';
    const match = ownerOptions.find(x => x.type === o.type && x.id === o.id);
    return match ? match.name : '(unknown owner)';
  };

  // Closing the breakout also clears any product-row selection highlight.
  const closeBreakout = opening => {
    if (controller.close(opening)) { setSelectedId(null); onCloseBreakout?.(); }
  };

  // Open the create/edit form in the settings-breakout (self-contained ProductForm
  // so it survives the breakout snapshotting its node). `key` remounts it when the
  // target changes. Opening a form clears the row selection (we're not viewing a
  // product anymore).
  const openProductForm = (prod) => {
    const opening = controller.open(prod?.id);
    if (!opening) return;
    activeOpening.current = opening;
    setSelectedId(null);
    onOpenBreakout?.(
      <ProductForm
        key={opening.key}
        initialProduct={prod || null}
        controller={controller}
        opening={opening}
        onClose={() => closeBreakout(opening)}
      />
    );
  };

  // Click a product row → highlight it and show its read-only detail in the breakout.
  const openProductDetail = (prod) => {
    const opening = controller.open(prod.id);
    if (!opening) return;
    activeOpening.current = opening;
    setSelectedId(prod.id);
    onOpenBreakout?.(
      <ProductDetailOpening
        key={opening.key}
        controller={controller}
        opening={opening}
        onEdit={openProductForm}
        onClose={() => closeBreakout(opening)}
      />
    );
  };

  const handleDelete = async (id) => {
    if (controller.blocked(id)) return;
    if (deleteArm.current?.id !== id || deleteArm.current?.controller !== controller) {
      deleteArm.current = { id, controller };
      setArmedDeleteProductId(id);
      clearTimeout(deleteTimer.current);
      deleteTimer.current = setTimeout(() => { deleteArm.current = null; setArmedDeleteProductId(null); }, 3000);
      return;
    }
    deleteArm.current = null;
    clearTimeout(deleteTimer.current);
    setArmedDeleteProductId(null);
    const deletionOpening = activeOpening.current;
    const result = await controller.mutation(id, config => deleteProduct(id, config), value => value?.message === 'Product deleted');
    if (!controller.owns()) return;
    if (result.kind === 'accepted') {
      controller.observeDeleted(id);
      if (activeOpening.current === deletionOpening && deletionOpening?.productId === id) closeBreakout(deletionOpening);
      controller.notice('Product deleted.');
    } else if (result.kind === 'unknown') {
      controller.notice('Deletion outcome is uncertain. Refresh the product list to inspect current state; deletion will not be repeated automatically.');
    } else if (result.kind === 'rejected') controller.notice('Deletion was refused. The catalog will be checked again.');
    if (result.kind !== 'blocked') {
      const refreshed = await controller.refreshList();
      if (!refreshed && result.kind === 'accepted') controller.notice('Product deleted, but the catalog refresh failed. Retry product list; deletion will not be repeated.');
    }
  };

  const filtered = products.filter(p => {
    if (ownerFilter !== 'all' && p.owner?.id !== ownerFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        p.product_code.toLowerCase().includes(q) ||
        (p.product_name || '').toLowerCase().includes(q) ||
        ownerLabel(p.owner).toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="products-section">
      {!catalog.active && <p role="status">Product catalog management requires an active online administrator session.</p>}
      {catalog.notice && <p role="status">{catalog.notice}</p>}
      {catalog.listState === 'loading' && <p>Loading products...</p>}
      {catalog.listState === 'unavailable' && <p role="alert">Products could not be loaded. Any displayed products are from the previous read.</p>}
      <Button variant="outline" disabled={!catalog.active} onClick={controller.refreshList}>Retry product list</Button>
      {catalog.ownerState === 'unavailable' && <p role="alert">Product owners could not be loaded. Existing products are still available.</p>}
      {catalog.ownerState !== 'available' && <Button variant="outline" disabled={!catalog.active} onClick={controller.refreshOwners}>Retry owner list</Button>}
      {catalog.writes.$create?.status === 'unknown' && <p role="alert">A creation outcome is uncertain. Inspect the catalog; another creation is blocked in this session to avoid duplicating that request.</p>}
      {/* Toolbar */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex gap-2 flex-1 w-full sm:w-auto">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search code or name..."
                className="pl-9"
                data-testid="products-search"
              />
            </div>
            <select
              value={ownerFilter}
              onChange={e => setOwnerFilter(e.target.value)}
              className="px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white text-stone-700"
              data-testid="products-owner-filter"
            >
              <option value="all">All Owners</option>
              {ownerOptions.map(o => (
                <option key={`${o.type}:${o.id}`} value={o.id}>
                  {o.name}{o.type === 'florist' ? ' (Florist)' : ''}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              const opening = controller.open();
              if (!opening) return;
              activeOpening.current = opening;
              setSelectedId(null);
              onOpenBreakout?.(<SizesBreakout key={opening.key} opening={opening} onClose={() => closeBreakout(opening)} />);
            }}
            className="flex items-center gap-2"
            data-testid="edit-sizes-btn"
          >
            <Ruler className="h-4 w-4" /> Edit Sizes
          </Button>
        </div>
        <Button
          onClick={() => openProductForm(null)}
          disabled={!catalog.active || catalog.owners === null || controller.blocked('$create')}
          className="flex items-center gap-2"
          data-testid="add-product-btn"
        >
          <Plus className="h-4 w-4" /> Add Product
        </Button>
      </div>

      {/* List */}
      {catalog.products === null ? null : filtered.length === 0 ? (
        <div className="text-center py-12 text-stone-400">
          <Package className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">{products.length === 0 ? 'No products yet' : 'No matches'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {filtered.map(prod => {
            const isSelected = selectedId === prod.id;
            return (
              <div
                key={prod.id}
                onClick={() => openProductDetail(prod)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-stone-200 bg-white hover:bg-stone-50'
                }`}
                data-testid={`product-${prod.id}`}
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="font-mono text-sm font-semibold text-stone-800 truncate">
                    {prod.product_code}
                  </span>
                  <span className="text-xs text-stone-600 truncate">
                    {prod.product_name || '—'}
                  </span>
                </div>
                  <div className="flex flex-col items-start gap-1 flex-shrink-0">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        prod.owner?.type === 'florist'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-stone-100 text-stone-600'
                      }`}
                    >
                      {ownerLabel(prod.owner)}{prod.owner?.type === 'florist' ? ' · Florist' : ''}
                    </span>
                    <span className="text-xs text-stone-400">
                      {prod.variants?.length || 0} size{prod.variants?.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => openProductForm(prod)}
                      className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors"
                      data-testid={`edit-product-${prod.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(prod.id)}
                      disabled={controller.blocked(prod.id)}
                      className={
                        armedDeleteProductId === prod.id
                          ? 'inline-flex items-center gap-0.5 px-1.5 py-1.5 rounded-lg bg-red-600 text-white transition-colors'
                          : 'p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors'
                      }
                      title={armedDeleteProductId === prod.id ? 'Click again to confirm deletion' : 'Delete product'}
                      aria-label={armedDeleteProductId === prod.id ? 'Confirm product deletion' : 'Delete product'}
                      data-testid={`delete-product-${prod.id}`}
                    >
                      {armedDeleteProductId === prod.id ? (
                        <>
                          <AlertTriangle className="h-3.5 w-3.5" />
                          <span className="text-[9px] leading-none">Sure?</span>
                        </>
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductsPanel;
